import React, { useState } from 'react';

import { AnimateSharedLayout } from '@sopranodesign/framer-motion';
import {
  Flex,
  Button,
  InfoSection,
  Panel,
  TextInput,
  Label,
  TabButton,
  Divider,
  Tooltip,
  Accordion,
  Paragraph
} from '@sopranodesign/pulse-components';
import { ImageSelect } from '@sopranodesign/pulse-files';
import { useLanguages, LanguagesConsumer } from '@sopranodesign/pulse-hooks';

import { useBotSettings } from '../../hooks/index';

const FullPageGeneralSettings = () => {
  const [selectImage, setSelectImage] = useState(false);

  const {
    settings,
    setSettings,
    language,
    setLanguage,
    name,
    focusBot,
    handleNewImage
  } = useBotSettings();

  const { languages } = useLanguages();

  const showSelectImage = () => setSelectImage(true);

  const hideSelectImage = () => setSelectImage(false);

  const handleShowNameToggle = () => {
    setSettings(settings => ({
      ...settings,
      name_enabled: !settings.name_enabled
    }));
  };

  const handleNameChange = event => {
    const newName = event.target.value;

    setSettings(settings => ({
      ...settings,
      names: settings.names.map(name => {
        if (name.language_id === language.id) {
          return {
            language_id: language.id,
            content: {
              name: newName
            }
          };
        }

        return name;
      })
    }));
  };

  const handleShowLogoToggle = () => {
    focusBot();

    setSettings(settings => ({
      ...settings,
      logo_enabled: !settings.logo_enabled
    }));
  };

  const handleImageChange = file => {
    handleNewImage(file);
    hideSelectImage();
  };

  const handleLogoLinkChange = event => {
    const value = event.target.value;

    setSettings(settings => ({
      ...settings,
      logo_link: value
    }));
  };

  const handleResetBotOnClose = () => {
    setSettings(settings => ({
      ...settings,
      reset_on_close: !settings.reset_on_close
    }));
  };

  return (
    <Flex>
      <Panel>
        <InfoSection
          mtNone
          title="Main Settings"
          info="Add a logo to your bot."
        />
        <Divider />

        {selectImage && (
          <ImageSelect
            handleCancel={hideSelectImage}
            handleFileSelect={handleImageChange}
          />
        )}

        <Flex col ySpace>
          <LanguagesConsumer>
            <Flex>
              <AnimateSharedLayout>
                {languages.map(languageTab => (
                  <TabButton
                    key={languageTab.id}
                    active={languageTab.id === language.id}
                    onClick={() => setLanguage(languageTab)}
                  >
                    {languageTab.name}
                  </TabButton>
                ))}
              </AnimateSharedLayout>
            </Flex>

            <Divider fullWidth mtNone />
          </LanguagesConsumer>

          <Accordion
            header="Show chatbot name"
            primary
            hasToggle
            checked={settings.name_enabled}
            onChange={handleShowNameToggle}
          >
            <Label>Chatbot name</Label>

            <TextInput
              disabled={!settings.name_enabled}
              aria-label="bot-name"
              value={name}
              onChange={handleNameChange}
              onClick={focusBot}
              maxLength="255"
            />
          </Accordion>

          <Accordion
            header="Show chatbot logo"
            primary
            hasToggle
            checked={settings.logo_enabled}
            onChange={handleShowLogoToggle}
          >
            <Flex block col>
              <Flex>
                <Flex border center col mr pad="true" rounded>
                  <Label>Logo image</Label>
                  <Paragraph mb smallText colour="grey">
                    {settings.logo === null
                      ? 'No image selected'
                      : 'Current image:'}
                  </Paragraph>
                  {settings.logo && (
                    <img
                      alt="Bot logo"
                      style={{ width: '2rem', marginBottom: '1rem' }}
                      src={settings.logo}
                    />
                  )}
                  <Button
                    mtAuto
                    disabled={!settings.logo_enabled}
                    variant="secondary"
                    onClick={showSelectImage}>
                    Change Chatbot Logo
                  </Button>
                </Flex>
              </Flex>
            </Flex>

            <Flex col mt>
              <Label htmlFor="logo_link">
                Logo link
              </Label>

              <TextInput
                disabled={!settings.logo_enabled}
                id="logo_link"
                aria-label="logo link input"
                placeholder="https://website.com"
                value={settings.logo_link || ''}
                onChange={handleLogoLinkChange}
              />
            </Flex>
          </Accordion>

          <Accordion
            primary
            hasToggle
            checked={settings.reset_on_close}
            onChange={handleResetBotOnClose}
            header="Reset chatbot on close"
            actions={
              <Flex
                className="tooltip"
                style={{
                  marginInlineEnd: 'auto'
                }}
              >
                <Tooltip
                  tooltip={
                    <Tooltip.Content>
                      Reset the chatbot conversation
                      back to the start when the
                      widget is closed
                    </Tooltip.Content>
                  }
                >
                  <Tooltip.Icon>?</Tooltip.Icon>
                </Tooltip>
              </Flex>
            }
          />
        </Flex>
      </Panel>
    </Flex>
  );
};

export default FullPageGeneralSettings;